// calculationUtils.js
import nomHsCodes from "./nomHsCodes.json";

const normalizeHsCode = (value) => String(value || "").replace(/\D/g, "");

const matchesHsCodePrefixes = (items = [], prefixes = []) => {
    const normalizedPrefixes = prefixes
        .map(normalizeHsCode)
        .filter(prefix => prefix.length === 4 || prefix.length === 6);

    return items.some((item) => {
        const hsCode = normalizeHsCode(item.hsCode || item.itemHSCode);
        return normalizedPrefixes.some(prefix => hsCode.slice(0, prefix.length) === prefix);
    });
};

const getNomHsCodePrefixes = () => [
    ...(nomHsCodes.fourDigit || []),
    ...(nomHsCodes.sixDigit || [])
];

const isNomService = (service) => service?.description?.trim().toLowerCase() === "nom";
const HS_CODE_STARTS_WITH_KEYS = ["HSCode Starts with", "Shipment HS Code Starts With"];

const findHsCodeStartsWithIndex = (values = []) =>
    values.findIndex((value) => HS_CODE_STARTS_WITH_KEYS.includes(value));

export const getUniqueHSCodeCount = (items) => {
    const uniqueHSCodes = new Set(
        items.map((item) => item.itemHSCode || item.hsCode).filter(Boolean)
    );
    return uniqueHSCodes.size;
};

// Function to evaluate simple conditions (e.g., document-based conditions)
export const evaluateSimpleCondition = (formula,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty) => {
    // console.log('Starting evaluateSimpleCondition with:', {
    //     formula,
    //     values: JSON.stringify(values),
    //     calculatedValues: JSON.stringify(calculatedValues)
    // });

    const getDynamicValue = (valueKey) => {
        switch (valueKey.toLowerCase()) {
            case "document":
                // console.log("Inside evaluateSimpleCondition ", cif);

                return values.requiredDoc === 'Required' ? true : false;
            case "shipment value":
            case "invoice value":
                return totalItemsValue;
            case "quantity":
                return totalItemsQuantity;
            case "shipment weight":
            case "item weight":
                return totalDimensionsWeight;
            case "total item quantity":
                return itemsTotalQty;
            case "volume weight":
                const length = totalDimensionsLength;
                const width = totalDimensionsWidth;
                const height = totalDimensionsHeight;
                return (length * width * height) / 6000;
            case "cif":
                return cif;
            case "cif110":
                return totalItemsValue * 1.10;
            case "insurance":
                return insurance;
            case "hscode count":
                return getUniqueHSCodeCount(values.items);
            default:
                return calculatedValues[valueKey] !== undefined ?
                    calculatedValues[valueKey] :
                    parseFloat(valueKey) || 0;
        }
    };

    let index = 0;
    while (index < formula.length) {
        if (formula[index] === 'if') {
            index++;

            // Handle condition based on type
            const conditionType = formula[index].toLowerCase();

            // Boolean condition check (like 'document')
            if (['document'].includes(conditionType)) {
                const conditionValue = getDynamicValue(conditionType);
                index++;

                // Find 'then' keyword
                while (index < formula.length && formula[index] !== 'then') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'then') {
                    index++; // Move past 'then'

                    // Get the value after 'then'
                    const thenValue = getDynamicValue(formula[index]);

                    // If condition is true, return the 'then' value
                    if (conditionValue === true) {
                        // console.log(`Condition '${conditionType}' is true, returning ${thenValue}`);
                        return thenValue;
                    }

                    // Move index to look for 'else'
                    while (index < formula.length && formula[index] !== 'else') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'else') {
                        index++; // Move past 'else'

                        // Skip optional 'then' after 'else'
                        if (index < formula.length && formula[index] === 'then') {
                            index++;
                        }

                        // Get the value after 'else' or 'else then'
                        const elseValue = getDynamicValue(formula[index]);

                        // If condition is false, return the 'else' value
                        if (conditionValue === false) {
                            // console.log(`Condition '${conditionType}' is false, returning ${elseValue}`);
                            return elseValue;
                        }
                    }
                }
            }
            // Numeric comparison conditions (like value > number)
            else {
                const leftValue = getDynamicValue(formula[index]);
                index++;

                if (index < formula.length && ['>', '<', '>=', '<=', '=='].includes(formula[index])) {
                    const operator = formula[index];
                    index++;
                    const rightValue = getDynamicValue(formula[index]);
                    index++;

                    let conditionMet = false;
                    switch (operator) {
                        case '>': conditionMet = leftValue > rightValue; break;
                        case '<': conditionMet = leftValue < rightValue; break;
                        case '>=': conditionMet = leftValue >= rightValue; break;
                        case '<=': conditionMet = leftValue <= rightValue; break;
                        case '==': conditionMet = leftValue === rightValue; break;
                    }

                    // console.log(`Condition: ${leftValue} ${operator} ${rightValue}, Result: ${conditionMet}`);

                    // Find 'then' token
                    while (index < formula.length && formula[index] !== 'then') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'then') {
                        index++; // Move past 'then'

                        // Get the value after 'then'
                        const thenValue = getDynamicValue(formula[index]);

                        // If condition is met, return the 'then' value
                        if (conditionMet) {
                            // console.log(`Numeric condition met, returning ${thenValue}`);
                            return thenValue;
                        }

                        // Move index to look for 'else'
                        while (index < formula.length && formula[index] !== 'else') {
                            index++;
                        }

                        if (index < formula.length && formula[index] === 'else') {
                            index++; // Move past 'else'

                            // Skip optional 'then' after 'else'
                            if (index < formula.length && formula[index] === 'then') {
                                index++;
                            }

                            // Get the value after 'else' or 'else then'
                            const elseValue = getDynamicValue(formula[index]);

                            // If condition is not met, return the 'else' value
                            if (!conditionMet) {
                                // console.log(`Numeric condition not met, returning ${elseValue}`);
                                return elseValue;
                            }
                        }
                    }
                }
            }
        }
        index++;
    }

    // Default return if no matching condition found
    // console.log('No matching condition found, returning 0');
    return 0;
};

// Function to evaluate conditional formulas with min/max
export const evaluateConditionalFormula = (formula,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty) => {
    // console.log('Starting evaluateConditionalFormula with:', {
    //     formula,
    //     values: JSON.stringify(values),
    //     calculatedValues: JSON.stringify(calculatedValues)
    // });

    const getDynamicValue = (valueKey) => {
        switch (valueKey.toLowerCase()) {
            case "shipment value":
            case "invoice value":
                return totalItemsValue;
            case "quantity":
                return totalItemsQuantity;
            case "shipment weight":
            case "item weight":
                return totalDimensionsWeight;
            case "total item quantity":
                return itemsTotalQty;
            case "volume weight":
                const length = totalDimensionsLength;
                const width = totalDimensionsWidth;
                const height = totalDimensionsHeight;
                return (length * width * height) / 6000;
            case "cif":
                return cif;
            case "cif110":
                return totalItemsValue * 1.10;
            case "insurance":
                return insurance;
            case "hscode count":
                return getUniqueHSCodeCount(values.items);
            case "document":
                return values.requiredDoc === 'Required' ? true : false;
            default:
                return calculatedValues[valueKey] !== undefined ?
                    calculatedValues[valueKey] :
                    parseFloat(valueKey) || 0;
        }
    };

    // Process max/min function with comma-separated expressions
    const evaluateMinMax = (func, expressions) => {
        // console.log(`Evaluating ${func} with expressions:`, expressions);
        const results = expressions.map(expr => {
            const tokens = expr.trim().split(' ').filter(t => t);
            let result = 0;
            let operator = '+';

            for (let i = 0; i < tokens.length; i++) {
                if (['+', '-', '*', '/', '%'].includes(tokens[i])) {
                    operator = tokens[i];
                } else {
                    const value = getDynamicValue(tokens[i]);

                    switch (operator) {
                        case '+': result += value; break;
                        case '-': result -= value; break;
                        case '*': result *= value; break;
                        case '/': result /= value !== 0 ? value : 1; break;
                        case '%': result = (result * value) / 100; break;
                    }
                }
            }
            return result;
        });

        return func === 'min' ? Math.min(...results) : Math.max(...results);
    };

    // Find if/else-if/else blocks
    let index = 0;
    while (index < formula.length) {
        if (formula[index] === 'if') {
            // Parse condition
            index++;
            const conditionType = formula[index].toLowerCase();

            // Handle document type condition
            if (conditionType === 'document') {
                const isDocument = getDynamicValue('document');
                index++;

                // Find 'then' token
                while (index < formula.length && formula[index] !== 'then') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'then') {
                    index++;
                    // Handle then case - find function (max/min)
                    if (['max', 'min'].includes(formula[index])) {
                        const func = formula[index];
                        index++;

                        // Find opening parenthesis
                        while (index < formula.length && formula[index] !== '(') {
                            index++;
                        }
                        index++; // Skip '('

                        // Collect expressions until closing parenthesis
                        const expressions = [];
                        let currentExpr = '';
                        let parenCount = 1;

                        while (index < formula.length && parenCount > 0) {
                            if (formula[index] === '(') parenCount++;
                            else if (formula[index] === ')') parenCount--;

                            if (formula[index] === ',' && parenCount === 1) {
                                expressions.push(currentExpr);
                                currentExpr = '';
                            } else if (parenCount > 0) {
                                currentExpr += formula[index] + ' ';
                            }
                            index++;
                        }
                        if (currentExpr) expressions.push(currentExpr);

                        if (isDocument) {
                            return evaluateMinMax(func, expressions);
                        }
                    }
                }

                // If condition false, find 'else' token
                while (index < formula.length && formula[index] !== 'else') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'else') {
                    index++;
                    // Skip 'then' if it exists after else
                    if (index < formula.length && formula[index] === 'then') {
                        index++;
                    }

                    // Handle else case
                    if (['max', 'min'].includes(formula[index])) {
                        const func = formula[index];
                        index++;

                        // Find opening parenthesis
                        while (index < formula.length && formula[index] !== '(') {
                            index++;
                        }
                        index++; // Skip '('

                        // Collect expressions until closing parenthesis
                        const expressions = [];
                        let currentExpr = '';
                        let parenCount = 1;

                        while (index < formula.length && parenCount > 0) {
                            if (formula[index] === '(') parenCount++;
                            else if (formula[index] === ')') parenCount--;

                            if (formula[index] === ',' && parenCount === 1) {
                                expressions.push(currentExpr);
                                currentExpr = '';
                            } else if (parenCount > 0) {
                                currentExpr += formula[index] + ' ';
                            }
                            index++;
                        }
                        if (currentExpr) expressions.push(currentExpr);

                        if (!isDocument) {
                            return evaluateMinMax(func, expressions);
                        }
                    }
                }
            }
            // Handle numeric comparison conditions (e.g., 'Invoice Value' > 5000)
            else {
                const leftValue = getDynamicValue(formula[index]);
                index++;
                const operator = formula[index];
                index++;
                const rightValue = getDynamicValue(formula[index]);
                index++;

                let conditionMet = false;
                switch (operator) {
                    case '>': conditionMet = leftValue > rightValue; break;
                    case '<': conditionMet = leftValue < rightValue; break;
                    case '>=': conditionMet = leftValue >= rightValue; break;
                    case '<=': conditionMet = leftValue <= rightValue; break;
                    case '==': conditionMet = leftValue === rightValue; break;
                }

                // console.log(`Condition: ${formula[index - 3]} ${operator} ${formula[index - 1]}, Result: ${conditionMet}`);

                // Find 'then' token
                while (index < formula.length && formula[index] !== 'then') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'then') {
                    index++;
                    // Handle then case - find function (max/min)
                    if (['max', 'min'].includes(formula[index])) {
                        const func = formula[index];
                        index++;

                        // Find opening parenthesis
                        while (index < formula.length && formula[index] !== '(') {
                            index++;
                        }
                        index++; // Skip '('

                        // Collect expressions until closing parenthesis
                        const expressions = [];
                        let currentExpr = '';
                        let parenCount = 1;

                        while (index < formula.length && parenCount > 0) {
                            if (formula[index] === '(') parenCount++;
                            else if (formula[index] === ')') parenCount--;

                            if (formula[index] === ',' && parenCount === 1) {
                                expressions.push(currentExpr);
                                currentExpr = '';
                            } else if (parenCount > 0) {
                                currentExpr += formula[index] + ' ';
                            }
                            index++;
                        }
                        if (currentExpr) expressions.push(currentExpr);

                        if (conditionMet) {
                            return evaluateMinMax(func, expressions);
                        }
                    }
                }

                // If condition false, find else-if or else token
                while (index < formula.length &&
                    formula[index] !== 'else if' &&
                    formula[index] !== 'else') {
                    index++;
                }

                if (index < formula.length) {
                    if (formula[index] === 'else if') {
                        // Handle else-if case (recursive)
                        const remainingFormula = formula.slice(index);
                        return evaluateConditionalFormula(remainingFormula, values, calculatedValues);
                    } else if (formula[index] === 'else') {
                        index++;
                        // Skip 'then' if it exists after else
                        if (index < formula.length && formula[index] === 'then') {
                            index++;
                        }

                        // Handle else case
                        if (['max', 'min'].includes(formula[index])) {
                            const func = formula[index];
                            index++;

                            // Find opening parenthesis
                            while (index < formula.length && formula[index] !== '(') {
                                index++;
                            }
                            index++; // Skip '('

                            // Collect expressions until closing parenthesis
                            const expressions = [];
                            let currentExpr = '';
                            let parenCount = 1;

                            while (index < formula.length && parenCount > 0) {
                                if (formula[index] === '(') parenCount++;
                                else if (formula[index] === ')') parenCount--;

                                if (formula[index] === ',' && parenCount === 1) {
                                    expressions.push(currentExpr);
                                    currentExpr = '';
                                } else if (parenCount > 0) {
                                    currentExpr += formula[index] + ' ';
                                }
                                index++;
                            }
                            if (currentExpr) expressions.push(currentExpr);

                            if (!conditionMet) {
                                return evaluateMinMax(func, expressions);
                            }
                        }
                    }
                }
            }
        }
        index++;
    }

    // Default return if no matching condition found
    return 0;
};

// Function to evaluate complex mathematical formulas
export const evaluateComplexFormula = (
    formula,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty
) => {
    // console.log('Starting evaluateComplexFormula with:', {
    //     formula,
    //     values: JSON.stringify(values),
    //     calculatedValues: JSON.stringify(calculatedValues)
    // });
    const tokens = formula.flat();  // Flatten the array in case it's nested

    // console.log('Flattened tokens:', tokens);

    const precedence = {
        '*': 2,
        '/': 2,
        '+': 1,
        '-': 1,
        '%': 2
    };

    const applyOp = (op, b, a) => {
        // console.log(`Applying operation: ${a} ${op} ${b}`);
        switch (op) {
            case '+': return a + b;
            case '-': return a - b;
            case '*': return a * b;
            case '/': return b !== 0 ? a / b : Infinity;
            case '%': return (a * b) / 100;
        }
    };

    const getDynamicValue = (valueKey) => {
        switch (valueKey) {
            case "shipment value":
            case "Invoice Value":
                return totalItemsValue;
            case "shipment weight":
            case "Item Weight":
                return totalDimensionsWeight;
            case "Quantity":
                return totalItemsQuantity;
            case "Total Item Quantity":
                return itemsTotalQty;
            case "Volume Weight":
                const length = totalDimensionsLength;
                const width = totalDimensionsWidth;
                const height = totalDimensionsHeight;
                const volumetricWeight = (length * width * height) / 6000;
                return Math.max(totalDimensionsWeight, volumetricWeight);
            case "CIF":
                return cif;
            case "CIF110":
                return totalItemsValue * 1.10;
            case "Insurance":
                return insurance;
            case "HSCode Count":
                return getUniqueHSCodeCount(values.items);
            default:
                return calculatedValues[valueKey] !== undefined ? calculatedValues[valueKey] : parseFloat(valueKey) || 0;
        }
    };

    const evaluate = () => {
        const values = [];
        const ops = [];

        // console.log('Starting evaluation of tokens');
        for (let i = 0; i < tokens.length; i++) {
            // console.log(`Processing token: ${tokens[i]}`);
            if (tokens[i] === '(') {
                // console.log('Pushing opening parenthesis');
                ops.push(tokens[i]);
            }
            else if (tokens[i] === ')') {
                // console.log('Closing parenthesis encountered');
                while (ops.length && ops[ops.length - 1] !== '(') {
                    let op = ops.pop();
                    let b = values.pop();
                    let a = values.pop();
                    values.push(applyOp(op, b, a));
                }
                ops.pop(); // Remove '('
                // console.log('Closed parenthesis block');
            }
            else if (['+', '-', '*', '/', '%'].includes(tokens[i])) {
                // console.log(`Operator found: ${tokens[i]}`);
                while (ops.length && precedence[ops[ops.length - 1]] >= precedence[tokens[i]]) {
                    let op = ops.pop();
                    let b = values.pop();
                    let a = values.pop();
                    values.push(applyOp(op, b, a));
                }
                ops.push(tokens[i]);
            }
            else {
                const dynamicValue = getDynamicValue(tokens[i]);
                values.push(dynamicValue);
            }

            // console.log('Current values stack:', values);
            // console.log('Current operators stack:', ops);
        }

        // console.log('Final evaluation of remaining operators');
        while (ops.length) {
            let op = ops.pop();
            let b = values.pop();
            let a = values.pop();
            values.push(applyOp(op, b, a));
            // console.log('After final operation - values:', values);
        }

        // console.log('Final result:', values[0]);
        return values[0];
    };

    return evaluate();
};

// Main function to calculate service value
export const calculateServiceValue = (
    service,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty
) => {

    // console.log('Starting calculateServiceValue with:', {
    //     service: JSON.stringify(service),
    //     values: JSON.stringify(values),
    //     calculatedValues: JSON.stringify(calculatedValues)
    // });

    const nomHsCodeConditionIndex = isNomService(service) ? findHsCodeStartsWithIndex(service.values) : -1;

    if (nomHsCodeConditionIndex !== -1) {
        const hsCodeMatches = matchesHsCodePrefixes(values.items, getNomHsCodePrefixes());
        const thenIndex = service.values.indexOf("then", nomHsCodeConditionIndex);
        const elseIndex = service.values.indexOf("else", thenIndex);
        const formulaStartIndex = hsCodeMatches ? thenIndex + 1 : elseIndex + 1;
        const formulaEndIndex = hsCodeMatches && elseIndex !== -1 ? elseIndex : service.values.length;

        if (formulaStartIndex > 0) {
            return calculateServiceValue(
                { values: service.values.slice(formulaStartIndex, formulaEndIndex) },
                values,
                calculatedValues,
                totalItemsValue,
                totalDimensionsWeight,
                totalDimensionsLength,
                totalDimensionsWidth,
                totalDimensionsHeight,
                cif,
                insurance,
                totalItemsQuantity,
                itemsTotalQty
            );
        }

        return 0;
    }

    if (service.values.includes('if') &&
        (service.values.includes('max') || service.values.includes('min')) &&
        (service.values.includes('then') || service.values.includes('else'))) {
        return evaluateConditionalFormula(service.values,
            values,
            calculatedValues,
            totalItemsValue,
            totalDimensionsWeight,
            totalDimensionsLength,
            totalDimensionsWidth,
            totalDimensionsHeight,
            cif,
            insurance,
            totalItemsQuantity,
            itemsTotalQty);
    }
    // Add this check in the calculateServiceValue function, after your existing checks for other formula types
    if (service.values.includes('if') && service.values.includes('document') && !service.values.includes('max') && !service.values.includes('min')) {
        // This seems to be a simple condition with direct value assignment
        return evaluateSimpleCondition(service.values,
            values,
            calculatedValues,
            totalItemsValue,
            totalDimensionsWeight,
            totalDimensionsLength,
            totalDimensionsWidth,
            totalDimensionsHeight,
            cif,
            insurance,
            totalItemsQuantity,
            itemsTotalQty);
    }

    if (!service.values || service.values.length === 0) return 0;
    if (service.description === 'Insurance' && values.insuranceSelected !== 'YES') {
        return 0;
    }
    let result = 0;
    let operator = "+";
    let lastValue = 0;

    // Formulae Step 5 start:

    const evaluateComplexCondition = (formulaSlice) => {
        let index = 0;

        const getValue = (key) => {
            switch (key) {
                case 'shipment value':
                case 'Invoice Value':
                case 'Shipment Value':
                    return totalItemsValue;
                case 'shipment weight':
                case 'Item Weight':
                    return totalDimensionsWeight;
                case "Quantity":
                    return totalItemsQuantity;
                case "Total Item Quantity":
                    return itemsTotalQty;
                case "Volume Weight":
                    const length = totalDimensionsLength;
                    const width = totalDimensionsWidth;
                    const height = totalDimensionsHeight;
                    const volumetricWeight = (length * width * height) / 6000;
                    return Math.max(totalDimensionsWeight, volumetricWeight);
                case "CIF":
                    return cif;
                case "CIF110":
                    return totalItemsValue * 1.10;
                case "Insurance":
                    return insurance;
                case "HSCode Count":
                    return getUniqueHSCodeCount(values.items);
                default:
                    return calculatedValues[key] !== undefined ? calculatedValues[key] : parseFloat(key) || 0;
            }
        };

        const evaluateCondition = (value1, operator, value2) => {
            switch (operator) {
                case '>': return value1 > value2;
                case '<': return value1 < value2;
                case '>=': return value1 >= value2;
                case '<=': return value1 <= value2;
                case '==': return value1 === value2;
                default: return false;
            }
        };

        const evaluateExpression = (startIndex) => {
            let result = getValue(formulaSlice[startIndex]);
            let i = startIndex + 1;

            while (i < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[i])) {
                const operator = formulaSlice[i];
                i++;
                const value = getValue(formulaSlice[i]);
                i++;

                switch (operator) {
                    case '+': result += value; break;
                    case '-': result -= value; break;
                    case '*': result *= value; break;
                    case '/': result /= value !== 0 ? value : 1; break;
                    case '%': result = (result * value) / 100; break;
                }
            }

            return result;
        };

        while (index < formulaSlice.length) {
            if (formulaSlice[index] === 'if' || formulaSlice[index] === 'else if') {
                index++;
                const conditionType = formulaSlice[index];

                if (HS_CODE_STARTS_WITH_KEYS.includes(conditionType) || conditionType === 'HSCode') {
                    const prefixes = [];
                    index++;

                    if (formulaSlice[index] === 'Starts with') {
                        index++;
                    }

                    while (index < formulaSlice.length && formulaSlice[index] !== 'then') {
                        if (formulaSlice[index] !== ',') {
                            prefixes.push(formulaSlice[index]);
                        }
                        index++;
                    }

                    const hsCodeMatches = checkHSCodeStartsWith(values.items, prefixes);

                    if (formulaSlice[index] === 'then') {
                        index++;

                        if (hsCodeMatches) {
                            return evaluateExpression(index);
                        }

                        while (index < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[index])) {
                            index++;
                        }
                    }

                    continue;
                }

                const value1 = getValue(conditionType);
                index++;
                const operator = formulaSlice[index];
                index++;
                const value2 = getValue(formulaSlice[index]);
                index++;

                if (evaluateCondition(value1, operator, value2)) {
                    index++; // Skip 'then'
                    return evaluateExpression(index);
                } else {
                    // Skip to next 'else if' or 'else'
                    while (index < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[index])) {
                        index++;
                    }
                }
            } else if (formulaSlice[index] === 'else') {
                index += 2; // Skip 'else' and 'then'
                return evaluateExpression(index);
            } else {
                index++;
            }
        }

        return null;
    };

    // Ending Step 5 Formulae.

    const checkHSCodeStartsWith = (items, prefixes) => {
        const prefixArray = service.description?.trim().toLowerCase() === "nom"
            ? getNomHsCodePrefixes()
            : (Array.isArray(prefixes) ? prefixes : [prefixes]);

        return matchesHsCodePrefixes(items, prefixArray);
    };

    const getDynamicValue = (valueKey) => {
        switch (valueKey) {
            case "Invoice Value":
            case "Shipment Value":
                return totalItemsValue;

            case "Item Weight":
                return totalDimensionsWeight;
            case "Volume Weight":
                const length = totalDimensionsLength;
                const width = totalDimensionsWidth;
                const height = totalDimensionsHeight;
                const volumetricWeight = (length * width * height) / 6000;
                return Math.max(totalDimensionsWeight, volumetricWeight);

            case "CIF":
                return cif;

            case "CIF110":
                return totalItemsValue * 1.10;

            case "Insurance":
                return insurance;
            case "Quantity":
                return totalItemsQuantity;
            case "Total Item Quantity":
                return itemsTotalQty;
            case "HSCode Count":
                return getUniqueHSCodeCount(values.items);
            default:
                // Check if it's a reference to another service
                if (calculatedValues[valueKey] !== undefined) {
                    return calculatedValues[valueKey];
                }
                // If it's not a special keyword, treat it as a numeric value
                return parseFloat(valueKey) || 0;
        }
    };


    const evaluateIfCondition = (values, index) => {
        // console.log('Evaluating if condition');
        const value1Key = values[index + 1];
        const operator = values[index + 2];
        const value2Key = values[index + 3];
        const thenKeyword = values[index + 4];
        const thenValue = values[index + 5];

        const value1 = getDynamicValue(value1Key);
        const value2 = getDynamicValue(value2Key);

        // console.log(`Condition: ${value1Key}(${value1}) ${operator} ${value2Key}(${value2})`);

        let conditionMet = false;
        switch (operator) {
            case ">":
                conditionMet = value1 > value2;
                break;
            case "<":
                conditionMet = value1 < value2;
                break;
            case ">=":
                conditionMet = value1 >= value2;
                break;
            case "<=":
                conditionMet = value1 <= value2;
                break;
            case "==":
                conditionMet = value1 === value2;
                break;
        }

        // console.log(`Condition met: ${conditionMet}`);

        if (conditionMet && thenKeyword === "then") {
            const result = getDynamicValue(thenValue);
            // console.log(`Returning then value: ${result}`);
            return result;
        } else {
            // console.log('Condition not met, returning null');
            return null;
        }
    };
    const evaluateExpression = (expressionArray) => {
        let localResult = 1; // Initialize to 1 for multiplication
        let localOperator = "*"; // Start with multiplication

        for (let j = 0; j < expressionArray.length; j++) {
            let item = expressionArray[j];

            // Handle multi-word dynamic values
            if (item === "Invoice" && expressionArray[j + 1] === "Value") {
                item = "Invoice Value";
                j++; // Skip the next item
            }
            if (item === "Shipment" && expressionArray[j + 1] === "Value") {
                item = "Shipment Value";
                j++; // Skip the next item
            }
            if (item === "Item" && expressionArray[j + 1] === "Weight") {
                item = "Item Weight";
                j++; // Skip the next item
            }
            if (item === "Quantity" && expressionArray[j + 1] === "Quantity") {
                item = "Quantity";
                j++;
            }
            if (item === "Total Item Quantity" && expressionArray[j + 1] === "Quantity") {
                item = "Total Item Quantity";
                j++;
            }
            if (item === "Volume" && expressionArray[j + 1] === "Weight") {
                item = "Volume Weight";
                j++; // Skip the next item
            }
            if (item === "CIF" && expressionArray[j + 1] === "CIF") {
                item = "CIF";
                j++; // Skip the next item
            }
            if (item === "CIF110" && expressionArray[j + 1] === "CIF110") {
                item = "CIF110";
                j++; // Skip the next item
            }
            if (item === "Insurance" && expressionArray[j + 1] === "Insurance") {
                item = "Insurance";
                j++; // Skip the next item
            }
            if (item === "HSCode" && expressionArray[j + 1] === "Count") {
                item = "HSCode Count";
                j++; // Skip the next item
            }
            if (item === "," || item === "(" || item === ")") continue;

            if (["*", "/", "+", "-", "%"].includes(item)) {
                localOperator = item;
            } else {
                const value = getDynamicValue(item);
                // console.log(`Item: ${item}, Value: ${value}, Operator: ${localOperator}`); // Debugging log
                switch (localOperator) {
                    case "+": localResult += value; break;
                    case "-": localResult -= value; break;
                    case "*": localResult *= value; break;
                    case "/": localResult /= value !== 0 ? value : 1; break;
                    case "%":
                        if (localResult === 1) {
                            // For percentage calculations, use the first value as the base
                            localResult = (value * localResult) / 100;
                        } else {
                            localResult = (localResult * value) / 100;
                        }
                        break;
                }
            }
        }
        // console.log(`Expression result: ${localResult}`); // Debugging log
        return localResult;
    };

    const evaluateHSCodeCondition = (condition, count) => {
        const [operator, value] = condition.split(" ");
        switch (operator) {
            case ">":
                return count > Number(value);
            case "<":
                return count < Number(value);
            case ">=":
                return count >= Number(value);
            case "<=":
                return count <= Number(value);
            case "==":
                return count === Number(value);
            default:
                return false;
        }
    };

    for (let i = 0; i < service.values.length; i++) {
        const item = service.values[i];
        // console.log(`Processing item at index ${i}: ${item}`);


        if (item === "if") {
            const complexResult = evaluateComplexCondition(service.values.slice(i));
            if (complexResult !== null) {
                return complexResult;
            }
        }

        if (item === "min" || item === "max") {
            // console.log(`Processing ${item} expression`); // Debugging log
            const expressionStart = service.values.indexOf("(", i);
            const expressionEnd = service.values.lastIndexOf(")");
            if (expressionStart !== -1 && expressionEnd !== -1) {
                const expressionArray = service.values.slice(expressionStart + 1, expressionEnd);
                const expressions = expressionArray.join(' ').split(',').map(expr => expr.trim());
                // console.log(`Expressions to evaluate: ${JSON.stringify(expressions)}`); // Debugging log
                const results = expressions.map(expr => evaluateExpression(expr.split(' ')));
                // console.log(`Results: ${JSON.stringify(results)}`); // Debugging log
                result = item === "min"
                    ? Math.min(...results)
                    : Math.max(...results);
                return Number(result.toFixed(2)); // Round to 2 decimal places
            }
        }

        if (service.values.includes('(')) {
            return evaluateComplexFormula(service.values, values, calculatedValues);
        }


        if (item === "if") {
            // const ifResult = evaluateIfCondition(service.values, i);
            // if (ifResult !== null) {
            //   return ifResult;
            // }

            const condition = service.values[i + 1];
            const value = service.values[i + 2];
            const keyword = service.values[i + 3];

            if (HS_CODE_STARTS_WITH_KEYS.includes(condition)) {
                // Support both single and multiple HSCode prefixes
                const prefixes = [];
                let j = i + 2;
                while (j < service.values.length && service.values[j] !== 'then') {
                    if (service.values[j] !== ',') {
                        prefixes.push(service.values[j]);
                    }
                    j++;
                }

                // console.log('HSCode Starts with prefixes:', prefixes);
                const hsCodeMatches = checkHSCodeStartsWith(values.items, prefixes);

                if (hsCodeMatches) {
                    // Find the 'then' index and process the formula
                    const thenIndex = service.values.indexOf('then', i);
                    const elseIndex = service.values.indexOf('else', thenIndex);
                    const formulaEndIndex = elseIndex !== -1 ? elseIndex : service.values.length;

                    const subFormula = {
                        values: service.values.slice(thenIndex + 1, formulaEndIndex),
                    };

                    return calculateServiceValue(subFormula, values, calculatedValues);
                } else if (service.values.includes('else')) {
                    // Process else clause if no match and else exists
                    const elseIndex = service.values.indexOf('else');
                    const subFormula = {
                        values: service.values.slice(elseIndex + 1),
                    };

                    return calculateServiceValue(subFormula, values, calculatedValues);
                }

                // If no match and no else clause, return 0
                return 0;
            }
        }

        if (item === "HSCode Count") {
            const condition = service.values[i + 1] + " " + service.values[i + 2];
            const keyword = service.values[i + 3];
            const hsCodeCount = getUniqueHSCodeCount(values.items);

            // console.log("Processing HSCode Count:", {
            //   condition,
            //   keyword,
            //   hsCodeCount,
            //   values: service.values,
            //   index: i
            // });

            if (evaluateHSCodeCondition(condition, hsCodeCount)) {
                // console.log("Condition met for HSCode Count:", condition);
                // If condition matches, use the formula after the keyword
                const formulaStartIndex = i + 4;
                const elseIndex = service.values.indexOf("else", formulaStartIndex);
                const formulaEndIndex =
                    elseIndex !== -1 ? elseIndex : service.values.length;

                // console.log("HSCode Count formula:", service.values.slice(formulaStartIndex, formulaEndIndex));

                const subFormula = {
                    values: service.values.slice(formulaStartIndex, formulaEndIndex),
                };

                return calculateServiceValue(subFormula, values, calculatedValues);
            } else if (service.values.includes("else")) {
                // console.log("Condition not met, executing else clause");
                // If condition doesn't match and there's an else clause, use the else part
                const elseIndex = service.values.indexOf("else");
                const subFormula = {
                    values: service.values.slice(elseIndex + 1),
                };

                return calculateServiceValue(subFormula, values, calculatedValues);
            }
            // console.log("No condition met and no else clause, returning 0");
            // If no condition is met and no else clause, return 0
            return 0;
        }

        if (item === "COO") {
            // Handle COO logic
            const country = service.values[i + 1];
            const keyword = service.values[i + 2];
            const matchingItem = values.items.find(
                (item) => item.countryOrigin === country
            );

            if (matchingItem) {
                // If country matches, use the formula after the keyword
                const formulaStartIndex = i + 3;
                const elseIndex = service.values.indexOf("else", formulaStartIndex);
                const formulaEndIndex =
                    elseIndex !== -1 ? elseIndex : service.values.length;

                const subFormula = {
                    values: service.values.slice(formulaStartIndex, formulaEndIndex),
                };

                result = calculateServiceValue(subFormula, values, calculatedValues);
            } else if (service.values.includes("else")) {
                // If country doesn't match and there's an else clause, use the else part
                const elseIndex = service.values.indexOf("else");
                const subFormula = {
                    values: service.values.slice(elseIndex + 1),
                };

                result = calculateServiceValue(subFormula, values, calculatedValues);
            }

            // Skip to the end of the COO block
            return result;
        }

        if (i % 2 === 0) {
            // It's a value or variable
            let numValue;
            // console.log(`Calculating value for: ${item}`);
            switch (item) {
                case "Invoice Value":
                case "Shipment Value":
                    numValue = totalItemsValue;
                    break;
                case "Item Weight":
                    numValue = totalDimensionsWeight;
                    break;
                case "Volume Weight":
                    const length = totalDimensionsLength;
                    const width = totalDimensionsWidth;
                    const height = totalDimensionsHeight;
                    const volumetricWeight = (length * width * height) / 6000;
                    numValue = Math.max(totalDimensionsWeight, volumetricWeight);
                    break;
                case "CIF":
                    numValue = cif;
                    break;
                case "CIF110":
                    numValue = totalItemsValue * 1.10;
                    break;
                case "Insurance":
                    numValue = insurance;
                    break;
                case "Quantity":
                    numValue = totalItemsQuantity;
                    break;
                case "Total Item Quantity":
                    numValue = itemsTotalQty;
                    break;
                case "HSCode Count":
                    numValue = getUniqueHSCodeCount(values.items);
                    break;
                default:
                    // Check if it's a reference to another service
                    if (calculatedValues[item]) {
                        numValue = calculatedValues[item];
                    } else {
                        numValue = parseFloat(item) || 0;
                    }
                    break;
            }

            // console.log(`Calculated numValue for ${item}: ${numValue}`);

            switch (operator) {
                case "+":
                    result += numValue;
                    break;
                case "-":
                    result -= numValue;
                    break;
                case "*":
                    result *= numValue;
                    break;
                case "/":
                    result /= numValue !== 0 ? numValue : 1; // Avoid division by zero
                    break;
                case ">":
                    lastValue = numValue;
                    break;
                case "%":
                    result = (result * numValue) / 100;
                    break;
            }
            // console.log(`After ${operator} operation, result: ${result}`);
        } else {
            // It's an operator
            operator = item;
            // console.log(`Set new operator: ${operator}`);
        }
    }

    if (operator === ">" && result < lastValue) {
        // console.log(`Applying > condition: setting result to ${lastValue}`);
        result = lastValue;
    }

    // console.log(`Final result for service calculation: ${result}`);
    return result;
};
